import React, { useMemo } from "react";
import {
  Platform,
  StyleSheet,
  TextInput,
  type StyleProp,
  type TextStyle,
} from "react-native";
import Colors from "../../../constants/colors";
import { inputAssistDisabledProps } from "./FloatingTextInputComponent";

export function estimateCurrencyInputWidth(
  value: string,
  fontSize: number,
): number {
  const text = value?.length ? value : "0.00";
  const charWidth = fontSize * 0.58;
  return Math.max(72, Math.min(text.length * charWidth + 12, 280));
}

export function formatAmountToTwoDecimals(text: string): string {
  const cleaned = text.replace(/[^0-9.]/g, "");
  if (!cleaned || cleaned === ".") return "";
  const n = parseFloat(cleaned);
  if (!isFinite(n)) return "";
  return n.toFixed(2);
}

export type CurrencyInputProps = {
  value: string;
  onChangeText: (text: string) => void;
  fontSize?: number;
  editable?: boolean;
  testID?: string;
  /** When true, appends `.00` on blur (Atlas-style) via `onChangeText`. */
  formatOnBlur?: boolean;
  onBlur?: () => void;
  placeholder?: string;
  style?: StyleProp<TextStyle>;
};

export function CurrencyInput({
  value,
  onChangeText,
  fontSize = 40,
  editable = true,
  testID,
  formatOnBlur = false,
  onBlur,
  placeholder = "0.00",
  style,
}: CurrencyInputProps) {
  const inputWidth = useMemo(
    () => estimateCurrencyInputWidth(value, fontSize),
    [value, fontSize],
  );

  const handleBlur = () => {
    if (formatOnBlur) {
      const formatted = formatAmountToTwoDecimals(value);
      if (formatted && formatted !== value) {
        onChangeText(formatted);
      }
    }
    onBlur?.();
  };

  return (
    <TextInput
      testID={testID}
      value={value}
      onChangeText={(text) => {
        if (/^\d*\.?\d{0,2}$/.test(text)) onChangeText(text);
      }}
      onBlur={handleBlur}
      keyboardType="decimal-pad"
      editable={editable}
      placeholder={placeholder}
      placeholderTextColor={Colors.subTitleColor}
      multiline={false}
      textAlign="right"
      style={[
        styles.input,
        {
          fontSize,
          lineHeight: Math.round(fontSize * 1.22),
          width: inputWidth,
        },
        style,
      ]}
      {...inputAssistDisabledProps}
    />
  );
}

const styles = StyleSheet.create({
  input: {
    fontFamily: "Rubik",
    fontWeight: "700",
    color: Colors.titleTextColor,
    textAlign: "right",
    flexGrow: 0,
    flexShrink: 1,
    padding: 0,
    backgroundColor: "transparent",
    ...(Platform.OS === "web"
      ? ({ outlineStyle: "none", outlineWidth: 0 } as any)
      : {}),
  },
});

export default CurrencyInput;
